import DownloadIcon from '@mui/icons-material/Download'
import SettingsIcon from '@mui/icons-material/Settings'
import { Box, Container, Grid } from '@mui/material'
import { useEffect, useState } from 'react'
import { Header } from '../../../components/Header'
import { useUserData } from '../../../user'
import { useLocalState } from '../../../utils/hooks/global/useLocalStorage'
import CustomButton from '../../global/CustomButton'
import CustomModal from '../../global/CustomModal'
import { Loading } from '../../global/Loading'
import AdminAssignChannel from './AdminAssignChannel'
import AdminChannelsTable from './AdminChannelsTable'
import AdminCreateChannelForm from './AdminCreateEditChannelForm'
import AdminDeleteChannel from './AdminDeleteChannel'
import AdminEditChannelForm from './AdminEditChannelForm'
import AdminSetChannelForm from './AdminSetChannelForm'
import AdminManageChannels from './AdminManageChannels'

function AdminChannels() {
  const [jwt] = useLocalState(null, 'jwt')
  const [channels, setChannels] = useState([])
  const [isModalOpen, setModalOpen] = useState(false)
  const [entities, setEntities] = useState([])
  const [profiles, setProfiles] = useState([])
  const [loading, setLoading] = useState(false)
  const [channel, setChannel] = useState()
  const [modalContent, setModalContent] = useState('')
  const [requestTrigger, setRequestTrigger] = useState(false)
  const backendUrl = process.env.REACT_APP_BACKEND_URL
  const userData = useUserData()

  const handleSubmitForm = () => {
    setModalOpen(false)
    setRequestTrigger(!requestTrigger)
  }

  const handleDelete = (channel) => {
    setChannel(channel)
    handleOpenModal('delete')
  }

  const handleSet = (channel) => {
    setChannel(channel)
    handleOpenModal('set')
  }

  const handleEdit = (channel) => {
    setChannel(channel)
    handleOpenModal('edit')
  }

  const handleManage = (channel) => {
    setChannel(channel)
    handleOpenModal('manage')
  }

  const handleOpenModal = (content) => {
    setModalContent(content)
    setModalOpen(true)
  }

  const handleExport = () => {
    let csvContent = 'data:text/csv;charset=utf-8,'
    csvContent +=
      'Chat Id;Channel Name;Channel Type;Entity Slug;Entity Name;Entity Type;Owner\n'

    channels.forEach((channel) => {
      csvContent +=
        [
          `${channel.chatId}`,
          `${channel.name}`,
          `${channel.channelType}`,
          `${channel.baseEntity ? channel.baseEntity.slug : ''}`,
          `${channel.baseEntity ? channel.baseEntity.name : ''}`,
          `${channel.baseEntity ? channel.baseEntity.entityType : ''}`,
          `${channel.owner ? channel.owner.user.email : ''}`
        ].join(';') + '\n'
    })

    const encodedUri = encodeURI(csvContent)
    const link = document.createElement('a')
    link.setAttribute('href', encodedUri)
    link.setAttribute('download', 'channels.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  useEffect(() => {
    const fetchEntities = fetch(`${backendUrl}/entities`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + jwt
      }
    }).then((response) => response.json())

    const fetchProfiles = fetch(`${backendUrl}/profiles`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + jwt
      }
    }).then((response) => response.json())

    const fetchChannels = fetch(
      `${backendUrl}/channels/?includeUser=true&includeEntity=true`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + jwt
        }
      }
    ).then((response) => response.json())

    Promise.all([fetchEntities, fetchProfiles, fetchChannels])
      .then((results) => {
        setEntities(results[0])
        setProfiles(results[1])
        setChannels(results[2])
        setLoading(false)
      })
      .catch((error) => {
        console.error('There was an error!', error)
        setLoading(false)
      })
  }, [jwt, backendUrl, requestTrigger])

  return (
    <>
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '75vh'
          }}
        >
          <Loading size={50} thickness={6} />
        </Box>
      ) : (
        <Container maxWidth={'xl'}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Header
                title="Channels"
                actions={
                  <>
                    {userData.isAdmin && (
                      <>
                        <CustomButton
                          color="blackOutlined"
                          buttonText={'Export'}
                          icon={DownloadIcon}
                          onClick={handleExport}
                          disabled={!channels}
                        />
                        {/* Bulk actions across all groups and migrations are
                            superadmin-only in the backend */}
                        {userData.isSuperadmin && (
                          <CustomButton
                            color={'blackOutlined'}
                            icon={SettingsIcon}
                            buttonText={'Manage'}
                            onClick={() => {
                              setChannel()
                              handleOpenModal('manage')
                            }}
                          />
                        )}
                        <CustomButton
                          type={'create'}
                          buttonText={'Create Channel'}
                          onClick={() => {
                            setChannel()
                            handleOpenModal('create')
                          }}
                        />
                      </>
                    )}
                  </>
                }
              />
            </Grid>

            <Grid item xs={12}>
              <AdminChannelsTable
                channels={channels}
                onModalOpen
                onSet={handleSet}
                onManage={handleManage}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            </Grid>
          </Grid>
          <CustomModal
            isOpen={isModalOpen}
            onClose={() => setModalOpen(false)}
            sx={{
              width: '40%',
              maxHeight: '80%',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none'
              }
            }}
          >
            {modalContent === 'create' && (
              <AdminCreateChannelForm
                onSubmit={handleSubmitForm}
                profiles={profiles}
                entities={entities}
              />
            )}
            {modalContent === 'manage' && (
              <AdminManageChannels
                channel={channel}
                profiles={profiles}
                channels={channels}
              />
            )}
            {modalContent === 'assign' && (
              <AdminAssignChannel
                channel={channel}
                profiles={profiles}
                onSubmit={() => handleSubmitForm()}
              />
            )}
            {modalContent === 'edit' && (
              <AdminEditChannelForm
                channel={channel}
                channels={channels}
                onSubmit={() => handleSubmitForm()}
              />
            )}
            {modalContent === 'delete' && (
              <AdminDeleteChannel
                channel={channel}
                profiles={profiles}
                onSubmit={() => handleSubmitForm()}
              />
            )}
            {modalContent === 'set' && (
              <AdminSetChannelForm
                channel={channel}
                profiles={profiles}
                entities={entities}
                onSubmit={() => handleSubmitForm()}
              />
            )}
          </CustomModal>
        </Container>
      )}
    </>
  )
}

export default AdminChannels
